import InfoIcon from "@mui/icons-material/InfoOutline";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import GenericImageUpload from "components/common/GenericImageUpload";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  providerSpecialtyOptions,
  UserProfileFormData,
} from "validations/userSchemas";

interface PracticeUserInformationSectionProps {
  profileImage: string;
}

const PracticeUserInformationSection: React.FC<
  PracticeUserInformationSectionProps
> = ({ profileImage }) => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<UserProfileFormData>();

  const { firstName, middleName, lastName } = watch();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <GenericImageUpload<UserProfileFormData>
          name="profileImage"
          label="Upload Profile Picture"
          showButton={false}
          defaultImage={profileImage}
        />
      </Box>
      <Typography
        variant="h6"
        sx={{
          fontSize: "1.5rem",
          fontWeight: 500,
          textAlign: "center",
          mt: 2,
        }}
      >
        {`${firstName || ""} ${middleName || ""} ${lastName || ""}`.trim() ||
          "Provider Name"}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="h6" sx={{ fontSize: "1.25rem", fontWeight: 500 }}>
          Practice Information
        </Typography>
        <Tooltip
          title="If you want to change your account email, please contact Cellera for further supports."
          arrow
          placement="right"
        >
          <IconButton
            sx={{
              color: "text.secondary",
              "&:hover": {
                color: "primary.main",
              },
            }}
          >
            <InfoIcon fontSize="medium" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Account ID */}
        <Controller
          name="id"
          control={control}
          render={({ field }) => (
            <TextField
              id="id"
              label="Account ID"
              variant="outlined"
              fullWidth
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
              {...field}
            />
          )}
        />

        {/* Account Email */}
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Box sx={{ position: "relative" }}>
              <TextField
                id="email"
                label="Account Email"
                variant="outlined"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
                {...field}
              />
            </Box>
          )}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 2,
          }}
        >
          {/* Provider First Name */}
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <TextField
                id="provider-first-name"
                label="Provider First Name"
                variant="outlined"
                fullWidth
                required
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                slotProps={{
                  htmlInput: {
                    maxLength: 64,
                  },
                }}
                {...field}
              />
            )}
          />

          {/* Provider Middle Name */}
          <Controller
            name="middleName"
            control={control}
            render={({ field }) => (
              <TextField
                id="provider-middle-name"
                label="Provider Middle Name"
                variant="outlined"
                fullWidth
                error={!!errors.middleName}
                helperText={errors.middleName?.message}
                slotProps={{
                  htmlInput: {
                    maxLength: 64,
                  },
                }}
                {...field}
              />
            )}
          />

          {/* Provider Last Name */}
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextField
                id="provider-last-name"
                label="Provider Last Name"
                variant="outlined"
                fullWidth
                required
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                slotProps={{
                  htmlInput: {
                    maxLength: 64,
                  },
                }}
                {...field}
              />
            )}
          />
        </Box>

        {/* Provider Phone Number */}
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field }) => (
            <TextField
              id="provider-phone-number"
              label="Provider Phone Number"
              variant="outlined"
              fullWidth
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber?.message}
              slotProps={{
                htmlInput: {
                  maxLength: 15,
                  pattern: "[0-9]*",
                  inputMode: "numeric",
                },
              }}
              onKeyDown={(e) => {
                // Only allow numeric characters
                if (
                  !/\d/.test(e.key) &&
                  e.key !== "Backspace" &&
                  e.key !== "Delete"
                ) {
                  e.preventDefault();
                }
              }}
              {...field}
            />
          )}
        />

        {/* NPI */}
        <Controller
          name="npi"
          control={control}
          render={({ field }) => (
            <TextField
              id="npi"
              label="NPI"
              variant="outlined"
              fullWidth
              required
              error={!!errors.npi}
              helperText={errors.npi?.message}
              slotProps={{
                htmlInput: {
                  maxLength: 10,
                  pattern: "[0-9]*",
                  inputMode: "numeric",
                },
              }}
              onKeyDown={(e) => {
                // Only allow numeric characters
                if (
                  !/\d/.test(e.key) &&
                  e.key !== "Backspace" &&
                  e.key !== "Delete"
                ) {
                  e.preventDefault();
                }
              }}
              {...field}
            />
          )}
        />

        {/* Provider Specialty */}
        <Controller
          name="specialties"
          control={control}
          render={({ field }) => {
            const handleSpecialtyChange = (
              specialty: string,
              checked: boolean,
            ) => {
              const currentValues = field.value || [];
              if (checked) {
                field.onChange([...currentValues, specialty]);
              } else {
                field.onChange(
                  currentValues.filter(
                    (value: string | undefined) =>
                      !!value && value !== specialty,
                  ),
                );
              }
            };

            return (
              <FormControl fullWidth error={!!errors.specialties}>
                <FormLabel
                  component="legend"
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "text.primary",
                    mb: 1,
                  }}
                >
                  Provider Specialty
                </FormLabel>
                <FormGroup>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(5, 1fr)",
                      gap: 1,
                    }}
                  >
                    {providerSpecialtyOptions.map((specialty) => (
                      <FormControlLabel
                        key={specialty}
                        control={
                          <Checkbox
                            checked={(field.value || []).includes(specialty)}
                            onChange={(e) =>
                              handleSpecialtyChange(specialty, e.target.checked)
                            }
                          />
                        }
                        label={specialty}
                        sx={{
                          fontSize: "0.875rem",
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.875rem",
                          },
                        }}
                      />
                    ))}
                  </Box>
                </FormGroup>
                {errors.specialties && (
                  <FormHelperText>{errors.specialties.message}</FormHelperText>
                )}
              </FormControl>
            );
          }}
        />
        {/* RXNT Integration Section */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 3 }}>
          <Typography
            variant="h6"
            sx={{ fontSize: "1.25rem", fontWeight: 500 }}
          >
            RXNT Configuration
          </Typography>
          <Tooltip
            title="RXNT credentials are used for EHR integration and prescription management."
            arrow
            placement="right"
          >
            <IconButton
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "primary.main",
                },
              }}
            >
              <InfoIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* RXNT Client ID */}
        <Controller
          name="rxntClientId"
          control={control}
          render={({ field }) => (
            <TextField
              id="rxnt-client-id"
              label="RXNT Client ID"
              variant="outlined"
              fullWidth
              error={!!errors.rxntClientId}
              helperText={
                errors.rxntClientId?.message ||
                "Your RXNT Client ID for EHR integration"
              }
              slotProps={{
                htmlInput: {
                  maxLength: 255,
                },
              }}
              {...field}
              value={field.value || ""}
            />
          )}
        />

        {/* RXNT Client Secret */}
        <Controller
          name="rxntClientSecret"
          control={control}
          render={({ field }) => (
            <TextField
              id="rxnt-client-secret"
              label="RXNT Client Secret"
              variant="outlined"
              fullWidth
              type="password"
              error={!!errors.rxntClientSecret}
              helperText={
                errors.rxntClientSecret?.message ||
                "Your RXNT Client Secret for EHR integration (sensitive data)"
              }
              slotProps={{
                htmlInput: {
                  maxLength: 255,
                },
              }}
              {...field}
              value={field.value || ""}
            />
          )}
        />

        {/* RXNT Doctor ID */}
        <Controller
          name="rxntDoctorId"
          control={control}
          render={({ field }) => (
            <TextField
              id="rxnt-doctor-id"
              label="RXNT Doctor ID"
              variant="outlined"
              fullWidth
              error={!!errors.rxntDoctorId}
              helperText={
                errors.rxntDoctorId?.message ||
                "Your RXNT Doctor ID for prescription management"
              }
              slotProps={{
                htmlInput: {
                  maxLength: 50,
                },
              }}
              {...field}
              value={field.value || ""}
            />
          )}
        />
      </Box>
    </Box>
  );
};

export default PracticeUserInformationSection;
